import { useEffect, useMemo } from 'react';
import { localDay } from '../../lib/localDay';
import { createPortal } from 'react-dom';
import { Wordmark, T, F } from '../../lib/bricks';
import { buildReport, reportTitle } from './siteReportModel';
import { humanDate } from './siteTimelineModel';
import { Button, Icon, KIND_ICON } from './siteUi';

// Paper is always light, on screen and in print.
// Theme-following sheet (index.css vars); printing always resolves to paper.
const P = { bg: 'var(--c-surf)', ink: 'var(--c-ink)', ink2: 'var(--c-ink-soft)', mute: 'var(--c-ink-mute)', hair: 'var(--c-hair-s)' };

const CSS = `
.sr-root{position:fixed;inset:0;z-index:2000;overflow-y:auto;background:rgba(20,18,16,0.72);-webkit-overflow-scrolling:touch}
.sr-bar{position:sticky;top:0;z-index:1;display:flex;justify-content:flex-end;gap:8px;padding:12px 16px;background:${T.night}}
.sr-paper{max-width:820px;margin:24px auto 64px;padding:56px 64px;box-sizing:border-box;background:${P.bg};color:${P.ink};font-family:${F.display}}
.sr-facts{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px 24px}
.sr-pair{display:grid;grid-template-columns:1fr 1fr;gap:12px}
.sr-photos{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:8px}
.sr-sec{margin-top:40px;break-inside:avoid;page-break-inside:avoid}
.sr-file{display:grid;grid-template-columns:20px minmax(0,1fr) auto;gap:12px;align-items:center;padding:12px 0;border-top:1px solid ${P.hair};break-inside:avoid}
@media (max-width:719px){
  .sr-root{background:var(--c-surf)}
  .sr-paper{margin:0;padding:32px 20px 48px}
  .sr-facts{grid-template-columns:repeat(2,minmax(0,1fr))}
  .sr-photos{grid-template-columns:repeat(2,minmax(0,1fr))}
  .sr-file{grid-template-columns:20px minmax(0,1fr)}
  .sr-file>.sr-host{display:none}
}
@media print{
  @page{margin:14mm}
  html,body{background:#fff !important}
  body>*:not(.sr-root){display:none !important}
  .sr-root{position:static !important;overflow:visible !important;background:#fff !important}
  .sr-bar{display:none !important}
  .sr-paper{max-width:none;margin:0;padding:0}
  .sr-facts{grid-template-columns:repeat(4,minmax(0,1fr))}
  .sr-photos{grid-template-columns:repeat(3,minmax(0,1fr))}
  .sr-file{grid-template-columns:20px minmax(0,1fr) auto}
  .sr-file>.sr-host{display:block}
  .sr-paper a{color:${P.ink};text-decoration:none}
}
`;

function H({ children }) {
  return <h2 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 500, color: P.ink }}>{children}</h2>;
}

function Fact({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 12.5, color: P.mute }}>{label}</div>
      <div style={{ fontSize: 15, marginTop: 4, color: P.ink, overflowWrap: 'anywhere' }}>{value}</div>
    </div>
  );
}

const shot = { display: 'block', width: '100%', aspectRatio: '4 / 3', objectFit: 'cover', background: 'var(--c-bg-soft)' };

// One visit as a printable report. Opens over the page; "Print or save PDF"
// prints only the paper. Images carry alt text only, never captions.
export default function SiteReport({ timeline, visitId, project, phases, onClose }) {
  const r = useMemo(() => buildReport(timeline, visitId, project, phases), [timeline, visitId, project, phases]);
  const visit = ((timeline && timeline.visits) || []).find((v) => v.id === visitId);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    const prevTitle = document.title;
    document.body.style.overflow = 'hidden';
    document.title = reportTitle(project, visit);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      document.title = prevTitle;
    };
  }, [onClose, project, visit]);

  if (!r) return null;
  const today = humanDate(localDay());

  return createPortal(
    <div className="sr-root" role="dialog" aria-modal="true" aria-label={`Site report, ${r.date}`} data-testid="site-report">
      <style>{CSS}</style>
      <div className="sr-bar">
        <Button tone="dark" icon="download" onClick={() => window.print()} data-testid="site-report-print">Print or save PDF</Button>
        <Button tone="dark" variant="secondary" icon="close" onClick={onClose} aria-label="Close report">Close</Button>
      </div>
      <article className="sr-paper">
        <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, paddingBottom: 24, borderBottom: `1px solid ${P.hair}` }}>
          <Wordmark size={18} color={P.ink} />
          <span style={{ fontSize: 13, color: P.mute }}>Site report</span>
        </header>

        <div style={{ marginTop: 32 }}>
          <h1 style={{ margin: 0, fontSize: 32, lineHeight: 1.1, fontWeight: 500, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{r.project.name}</h1>
          {r.project.place ? <p style={{ margin: '8px 0 0', fontSize: 15, color: P.ink2 }}>{r.project.place}</p> : null}
        </div>

        <div className="sr-facts sr-sec" style={{ marginTop: 32 }}>
          <Fact label="Visit" value={r.date} />
          <Fact label="Capture" value={r.capture} />
          {r.phase ? <Fact label="Phase" value={r.phase} /> : null}
          <Fact label="Status" value={r.status} />
        </div>

        <section className="sr-sec" data-testid="site-report-since">
          <H>Since last visit</H>
          <p style={{ margin: 0, fontSize: 15, color: P.ink2 }}>{r.since}</p>
        </section>

        <section className="sr-sec" data-testid="site-report-note">
          <H>Progress note</H>
          {r.note ? (
            <p style={{ margin: 0, fontSize: 15, lineHeight: 1.6, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{r.note.text}</p>
          ) : <p style={{ margin: 0, fontSize: 15, color: P.mute }}>No note for this visit.</p>}
        </section>

        {r.compare ? (
          <section className="sr-sec" data-testid="site-report-compare">
            <H>{r.compare.label}, then and now</H>
            <div className="sr-pair">
              {[r.compare.then, r.compare.now].map((f) => (
                <figure key={f.date} style={{ margin: 0 }}>
                  <img src={f.src} alt={`${r.compare.label}, ${humanDate(f.date)}`} style={{ ...shot, objectFit: 'contain' }} />
                  <figcaption style={{ marginTop: 8, fontSize: 13, color: P.mute }}>{humanDate(f.date)}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        ) : null}

        {r.photos.length ? (
          <section className="sr-sec" data-testid="site-report-photos" style={{ breakInside: 'auto', pageBreakInside: 'auto' }}>
            <H>Photos</H>
            <div className="sr-photos">
              {r.photos.map((m) => <img key={m.id} src={m.src} alt={m.alt} style={{ ...shot, breakInside: 'avoid' }} />)}
            </div>
            {r.morePhotos ? <p style={{ margin: '12px 0 0', fontSize: 13, color: P.mute }}>{`${r.morePhotos} more in the site file.`}</p> : null}
          </section>
        ) : null}

        <section className="sr-sec" data-testid="site-report-files" style={{ breakInside: 'auto', pageBreakInside: 'auto' }}>
          <H>Files</H>
          {r.files.length ? (
            <div style={{ borderBottom: `1px solid ${P.hair}` }}>
              {r.files.map((f) => (
                <a key={f.id} className="sr-file" href={f.url} target="_blank" rel="noreferrer" style={{ color: P.ink, textDecoration: 'none' }}>
                  <Icon name={KIND_ICON[f.kind] || 'file'} size={18} color={P.ink2} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 15, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.label}</span>
                    {f.label !== f.kindLabel ? <span style={{ display: 'block', fontSize: 13, color: P.mute }}>{f.kindLabel}</span> : null}
                  </span>
                  <span className="sr-host" style={{ fontSize: 13, color: P.mute }}>{f.host}</span>
                </a>
              ))}
            </div>
          ) : <p style={{ margin: 0, fontSize: 15, color: P.mute }}>Files appear after the visit is processed.</p>}
          {r.pending ? <p style={{ margin: '12px 0 0', fontSize: 13, color: P.mute }}>The 3D scan is still processing.</p> : null}
        </section>

        <footer style={{ marginTop: 48, paddingTop: 16, borderTop: `1px solid ${P.hair}`, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: 8, fontSize: 12.5, color: P.mute }}>
          <span>{r.credit} · brxs.ca</span>
          <span>{`Prepared ${today}`}</span>
        </footer>
      </article>
    </div>,
    document.body,
  );
}
